<template>
  <div>
    <Head :title="`Editar cuenta`" />
    
    <!-- Header -->
    <div class="flex items-center gap-4 mb-8">
      <button 
        class="p-3 text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg transition-colors"
        title="Volver atrás"
        @click="$inertia.visit('/')"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-3xl font-bold">
        <Link 
          class="text-indigo-600 hover:text-indigo-700 transition-colors" 
          href="/"
        >
          Cuenta
        </Link>
        <span class="text-gray-400 font-medium mx-2">/</span>
        <span>{{ form.name }}</span>
        <img v-if="logo" :src="logo" alt="Logo" class="h-8 ml-2 inline-block">
      </h1>
    </div>

    <!-- Tabs -->
    <div class="mb-8">
      <div class="border-b border-gray-200">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
            ]"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Tab Content -->
    <div v-if="activeTab === 'general'" class="max-w-3xl bg-white rounded-lg shadow-sm border border-gray-200">
      <!-- Formulario general existente -->
      <form @submit.prevent="update">
        <div class="p-8">
          <h2 class="text-lg font-bold mb-4">Información de la cuenta</h2>
          
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Datos básicos -->
            <text-input
              v-model="form.name"
              :error="form.errors.name"
              label="Nombre *"
              placeholder="Ingrese el nombre"
            />
            
            <text-input
              v-model="form.identification" 
              :error="form.errors.identification"
              label="Identificación *"
              placeholder="Ingrese la identificación"
            />

            <text-input
              v-model="form.email"
              :error="form.errors.email"
              type="email"
              label="Email *" 
              placeholder="correo@ejemplo.com"
            />

            <text-input
              v-model="form.phone"
              :error="form.errors.phone"
              label="Teléfono"
              placeholder="Ingrese el teléfono"
            />

            <text-input
              v-model="form.reasonName"
              :error="form.errors.reasonName"
              label="Razón Social"
              placeholder="Ingrese la razón social"
            />

            <text-input
              v-model="form.address"
              :error="form.errors.address"
              label="Dirección"
              placeholder="Ingrese la dirección"
            />

            <!-- Personalización -->
            <text-input
              v-model="form.sidebarColor"
              :error="form.errors.sidebarColor"
              type="color"
              label="Color del menú lateral"
            />

            <text-input
              v-model="form.textSidebarColor"
              :error="form.errors.textSidebarColor"
              type="color" 
              label="Color del texto del menú"
            />

            <text-input
              v-model="form.textHoverSidebarColor"
              :error="form.errors.textHoverSidebarColor"
              type="color"
              label="Color del texto al pasar el mouse"
            />

            <file-input
              v-model="form.logo"
              :error="form.errors.logo"
              accept="image/*"
              label="Logo de la empresa"
            />
          </div>
        </div>

        <div class="flex items-center justify-end gap-4 px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button
            type="button"
            class="btn-white"
            @click="$inertia.visit('/')"
          >
            Cancelar
          </button>
          <loading-button
            :loading="form.processing"
            class="btn-blue"
            type="submit"
          >
            <i class="fa fa-save mr-2"></i>
            Guardar cambios
          </loading-button>
        </div>
      </form>
      
    </div>

    <!-- Tab de Campos Adicionales -->
    <div v-if="activeTab === 'additionals'" class="space-y-6">
      <!-- Campos para Órdenes -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold">Campos adicionales para Órdenes</h3>
            <button
              @click="showAddFieldModal = true; currentEntityType = 'order'"
              class="btn-blue text-sm"
            >
              <i class="fa fa-plus mr-2"></i>
              Agregar campo
            </button>
          </div>
        </div>
        
        <div class="p-6">
          <div v-if="orderFields.length === 0" class="text-center text-gray-500 py-8">
            <i class="fa fa-list-alt text-4xl mb-4"></i>
            <p>No hay campos adicionales configurados para órdenes</p>
          </div>
          
          <div v-else class="space-y-4">
            <div
              v-for="field in orderFields"
              :key="field.id"
              class="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div class="flex-1">
                <div class="flex items-center gap-3">
                  <span class="font-medium">{{ field.field_label }}</span>
                  <span class="text-sm text-gray-500">({{ field.field_name }})</span>
                  <span class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">{{ field.field_type }}</span>
                  <span v-if="field.is_required" class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Requerido</span>
                </div>
              </div>
              
              <div class="flex items-center gap-2">
                <button
                  @click="editField(field)"
                  class="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                  title="Editar"
                >
                  <i class="fa fa-edit"></i>
                </button>
                <button
                  @click="deleteField(field)"
                  class="p-2 text-gray-600 hover:text-red-600 transition-colors"
                  title="Eliminar"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Campos para Clientes -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold">Campos adicionales para Clientes</h3>
            <button
              @click="showAddFieldModal = true; currentEntityType = 'client'"
              class="btn-blue text-sm"
            >
              <i class="fa fa-plus mr-2"></i>
              Agregar campo
            </button>
          </div>
        </div>
        
        <div class="p-6">
          <div v-if="clientFields.length === 0" class="text-center text-gray-500 py-8">
            <i class="fa fa-users text-4xl mb-4"></i>
            <p>No hay campos adicionales configurados para clientes</p>
          </div>
          
          <div v-else class="space-y-4">
            <div
              v-for="field in clientFields"
              :key="field.id"
              class="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div class="flex-1">
                <div class="flex items-center gap-3">
                  <span class="font-medium">{{ field.field_label }}</span>
                  <span class="text-sm text-gray-500">({{ field.field_name }})</span>
                  <span class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">{{ field.field_type }}</span>
                  <span v-if="field.is_required" class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Requerido</span>
                </div>
              </div>
              
              <div class="flex items-center gap-2">
                <button
                  @click="editField(field)"
                  class="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                  title="Editar"
                >
                  <i class="fa fa-edit"></i>
                </button>
                <button
                  @click="deleteField(field)"
                  class="p-2 text-gray-600 hover:text-red-600 transition-colors"
                  title="Eliminar"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab de Configuraciones -->
    <div v-if="activeTab === 'settings'" class="space-y-6">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="p-6 border-b border-gray-200">
          <h3 class="text-lg font-semibold">Configuraciones</h3>
        </div>
        <form @submit.prevent="update">
          <div class="p-6 space-y-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium">Deseas que el vendedor pueda modificar precios</p>
                <p class="text-sm text-gray-500">Habilita que los vendedores ajusten precios en sus pedidos.</p>
              </div>
              <button
                type="button"
                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                :class="form.sellerCanModifyPrices ? 'bg-indigo-600' : 'bg-gray-200'"
                @click="form.sellerCanModifyPrices = !form.sellerCanModifyPrices"
                :aria-pressed="form.sellerCanModifyPrices.toString()"
                aria-label="Toggle vendedor puede modificar precios"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                  :class="form.sellerCanModifyPrices ? 'translate-x-5' : 'translate-x-0'"
                ></span>
              </button>
            </div>

            <!-- <div class="flex items-center justify-between">
              <div>
                <p class="font-medium">Deseas que el vendedor tenga su propia agenda de clientes</p>
                <p class="text-sm text-gray-500">Cada vendedor gestiona una agenda de clientes independiente.</p>
              </div>
              <button
                type="button"
                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                :class="form.sellerHasOwnClientAgenda ? 'bg-indigo-600' : 'bg-gray-200'"
                @click="form.sellerHasOwnClientAgenda = !form.sellerHasOwnClientAgenda"
                :aria-pressed="form.sellerHasOwnClientAgenda.toString()"
                aria-label="Toggle agenda propia de clientes"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                  :class="form.sellerHasOwnClientAgenda ? 'translate-x-5' : 'translate-x-0'"
                ></span>
              </button>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium">Deseas que el vendedor pueda editar precios de productos</p>
                <p class="text-sm text-gray-500">Permite edición de precios directamente en el catálogo de productos.</p>
              </div>
              <button
                type="button"
                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                :class="form.sellerCanEditProductPrices ? 'bg-indigo-600' : 'bg-gray-200'"
                @click="form.sellerCanEditProductPrices = !form.sellerCanEditProductPrices"
                :aria-pressed="form.sellerCanEditProductPrices.toString()"
                aria-label="Toggle vendedor edita precios de productos"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                  :class="form.sellerCanEditProductPrices ? 'translate-x-5' : 'translate-x-0'"
                ></span>
              </button>
            </div> -->
          </div>

          <div class="flex items-center justify-end gap-4 px-8 py-4 bg-gray-50 border-t border-gray-100">
            <button
              type="button"
              class="btn-white"
              @click="$inertia.visit('/')"
            >
              Cancelar
            </button>
            <loading-button
              :loading="form.processing"
              class="btn-blue"
              type="submit"
            >
              <i class="fa fa-save mr-2"></i>
              Guardar cambios
            </loading-button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal para agregar/editar campos -->
    <div v-if="showAddFieldModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg max-w-md w-full mx-4">
        <div class="p-6 border-b border-gray-200">
          <h3 class="text-lg font-semibold">
            {{ editingField ? 'Editar campo' : 'Agregar campo' }}
          </h3>
        </div>
        
        <form @submit.prevent="saveField" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Tipo de entidad
            </label>
            <select
              v-model="fieldForm.entity_type"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              :disabled="!!editingField"
            >
              <option value="order">Órdenes</option>
              <option value="client">Clientes</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Nombre del campo *
            </label>
            <input
              v-model="fieldForm.field_name"
              type="text"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="ej: codigo_interno"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Etiqueta del campo *
            </label>
            <input
              v-model="fieldForm.field_label"
              type="text"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="ej: Código Interno"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Tipo de campo *
            </label>
            <select
              v-model="fieldForm.field_type"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            >
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="date">Fecha</option>
              <option value="select">Lista desplegable</option>
              <option value="textarea">Área de texto</option>
              <option value="boolean">Sí/No</option>
            </select>
          </div>
          
          <div v-if="fieldForm.field_type === 'select'">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Opciones (una por línea)
            </label>
            <textarea
              v-model="fieldOptionsText"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows="3"
              placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
            ></textarea>
          </div>
          
          <div class="flex items-center gap-4">
            <label class="flex items-center">
              <input
                v-model="fieldForm.is_required"
                type="checkbox"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span class="ml-2 text-sm text-gray-700">Campo requerido</span>
            </label>
            
            <label class="flex items-center">
              <input
                v-model="fieldForm.is_active"
                type="checkbox"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span class="ml-2 text-sm text-gray-700">Activo</span>
            </label>
          </div>
          
          <div class="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              @click="closeFieldModal"
              class="btn-white"
            >
              Cancelar
            </button>
            <loading-button
              :loading="savingField"
              class="btn-blue"
              type="submit"
            >
              {{ editingField ? 'Actualizar' : 'Crear' }}
            </loading-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  layout: Layout,
  
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    FileInput
  },

  props: {
    account: {
      type: Object,
      required: true
    },
    additionals: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      activeTab: 'general',
      logo: this.account.logo,
      showAddFieldModal: false,
      editingField: null,
      currentEntityType: 'order',
      savingField: false,
      
      tabs: [
        { id: 'general', name: 'Información General' },
        { id: 'additionals', name: 'Campos Adicionales' },
        { id: 'settings', name: 'Configuraciones'}
      ],
      
      form: this.$inertia.form({
        _method: 'put',
        name: this.account.name,
        identification: this.account.identification,
        email: this.account.email,
        phone: this.account.phone,
        reasonName: this.account.reasonName,
        address: this.account.address,
        sidebarColor: this.account.sidebarColor,
        textSidebarColor: this.account.textSidebarColor,
        textHoverSidebarColor: this.account.textHoverSidebarColor,
        logo: null,
        sellerCanModifyPrices: !!this.account.sellerCanModifyPrices,
        sellerHasOwnClientAgenda: !!this.account.sellerHasOwnClientAgenda,
        sellerCanEditProductPrices: !!this.account.sellerCanEditProductPrices
      }),
      
      fieldForm: this.$inertia.form({
        entity_type: 'order',
        field_name: '',
        field_label: '',
        field_type: 'text',
        field_options: [],
        is_required: false,
        is_active: true,
        sort_order: 0
      }),
      
      fieldOptionsText: ''
    }
  },

  computed: {
    orderFields() {
      return this.additionals.order || []
    },
    
    clientFields() {
      return this.additionals.client || []
    }
  },

  methods: {
    update() {
      this.form.post(`/accounts/${this.account.id}`, {
        onSuccess: () => {
          this.$notify({
            type: 'success',
            text: 'Cuenta actualizada correctamente'
          })
        }
      })
    },
    
    editField(field) {
      this.editingField = field
      this.currentEntityType = field.entity_type
      this.fieldForm = this.$inertia.form({
        entity_type: field.entity_type,
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        field_options: field.field_options || [],
        is_required: field.is_required,
        is_active: field.is_active,
        sort_order: field.sort_order
      })
      this.fieldOptionsText = (field.field_options || []).join('\n')
      this.showAddFieldModal = true
    },
    
    deleteField(field) {
      if (confirm('¿Estás seguro de que quieres eliminar este campo?')) {
        this.$inertia.delete(`/additionals/${field.id}`, {
          onSuccess: () => {
            this.$notify({
              type: 'success',
              text: 'Campo eliminado correctamente'
            })
          }
        })
      }
    },
    
    closeFieldModal() {
      this.showAddFieldModal = false
      this.editingField = null
      this.fieldForm.reset()
      this.fieldOptionsText = ''
    },
    
    saveField() {
      // Procesar opciones del campo select
      if (this.fieldForm.field_type === 'select') {
        this.fieldForm.field_options = this.fieldOptionsText
          .split('\n')
          .map(option => option.trim())
          .filter(option => option.length > 0)
      }
      
      this.savingField = true
      
      if (this.editingField) {
        // Actualizar campo existente
        this.fieldForm.put(`/additionals/${this.editingField.id}`, {
          onSuccess: () => {
            this.closeFieldModal()
            this.$notify({
              type: 'success',
              text: 'Campo actualizado correctamente'
            })
          },
          onFinish: () => {
            this.savingField = false
          }
        })
      } else {
        // Crear nuevo campo
        this.fieldForm.post('/additionals', {
          onSuccess: () => {
            this.closeFieldModal()
            this.$notify({
              type: 'success',
              text: 'Campo creado correctamente'
            })
          },
          onFinish: () => {
            this.savingField = false
          }
        })
      }
    }
  }
}
</script>